import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Radio, Users, Coins, Play, Plus, LogOut, 
  User, RefreshCw, AlertCircle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LiveHomePage() {
  const navigate = useNavigate();
  const { user, token, balance, isAuthenticated, login, logout, fetchBalance } = useAuth();
  
  const [liveStreams, setLiveStreams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [streamTitle, setStreamTitle] = useState('');
  const [streamPrice, setStreamPrice] = useState(1);
  const [creating, setCreating] = useState(false);

  // Fetch live streams
  const fetchStreams = async () => {
    try {
      const res = await axios.get(`${API}/streams/live`);
      setLiveStreams(res.data || []);
    } catch (e) {
      console.error('Failed to fetch streams:', e);
    }
  };

  // Fetch users for demo login
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/list`);
      setUsers(res.data.users || []);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchStreams(), fetchUsers()]);
      setLoading(false);
    };
    init();
    
    // Poll for new streams every 10 seconds
    const interval = setInterval(fetchStreams, 10000);
    return () => clearInterval(interval);
  }, []);

  // Handle login
  const handleLogin = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }
    
    const result = await login(selectedUser);
    if (result.success) {
      toast.success('Logged in successfully!');
      setShowLoginModal(false);
      fetchBalance();
    } else {
      toast.error(result.error);
    }
  };

  // Handle create stream
  const handleCreateStream = async () => {
    if (!streamTitle.trim()) {
      toast.error('Please enter a stream title');
      return;
    }
    
    setCreating(true);
    try {
      const res = await axios.post(`${API}/streams/create`, {
        title: streamTitle,
        tokens_per_minute: parseFloat(streamPrice)
      });
      
      toast.success('Stream created! Redirecting...');
      navigate(`/live/broadcast/${res.data.stream_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create stream');
    } finally {
      setCreating(false);
    }
  };

  // Watch stream
  const watchStream = (streamId) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    navigate(`/live/watch/${streamId}`);
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="live-home-page">
      <Toaster position="top-center" theme="dark" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                NaoView Live
              </h1>
              <p className="text-xs text-zinc-500">Stream & Earn</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full border border-zinc-800" data-testid="user-balance">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 font-bold">{balance.toFixed(2)}</span>
                </div>
                
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white font-bold rounded-full hover:bg-indigo-600 transition-all"
                  data-testid="go-live-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Go Live</span>
                </button>
                
                <button
                  onClick={logout}
                  className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white font-bold rounded-full hover:bg-indigo-600 transition-all"
                data-testid="login-btn"
              >
                <User className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Section header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Live Now
            </h2>
            <p className="text-zinc-500 text-sm">Watch live streams from creators</p>
          </div>
          
          <button
            onClick={fetchStreams}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900 text-zinc-400 rounded-lg hover:bg-zinc-800 hover:text-white transition-all"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        
        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="aspect-video bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : liveStreams.length > 0 ? (
          /* Stream grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {liveStreams.map((stream, i) => (
              <div
                key={stream.stream_id}
                onClick={() => watchStream(stream.stream_id)}
                className="group relative overflow-hidden rounded-xl bg-zinc-900 border border-zinc-800 hover:border-indigo-500/50 cursor-pointer transition-all duration-300"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`stream-card-${stream.stream_id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-gradient-to-br from-indigo-900/50 to-purple-900/50 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                  
                  {/* Live badge */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500 rounded-md">
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      <span className="text-white text-xs font-bold">LIVE</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-black/60 rounded-md">
                      <Users className="w-3 h-3 text-white" />
                      <span className="text-white text-xs font-medium">{stream.viewer_count}</span>
                    </div>
                  </div>
                  
                  {/* Price badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-md">
                    <Coins className="w-3 h-3 text-amber-400" />
                    <span className="text-amber-400 text-xs font-bold">{stream.tokens_per_minute}/min</span>
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {stream.profile_picture ? (
                      <img 
                        src={`https://dev.naofan.app/avatars/${stream.profile_picture}`}
                        alt={stream.broadcaster_name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {stream.broadcaster_name?.charAt(0) || 'N'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {stream.title}
                      </h3>
                      <p className="text-zinc-500 text-sm truncate">
                        {stream.broadcaster_name || 'Anonymous'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Radio className="w-10 h-10 text-zinc-700" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No Live Streams
            </h3>
            <p className="text-zinc-500 mb-6">Be the first to go live!</p>
            {isAuthenticated && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all"
              >
                Start Streaming
              </button>
            )}
          </div>
        )}
        
        {/* Info section */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
              <Play className="w-6 h-6 text-emerald-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              60 Seconds Free
            </h4>
            <p className="text-zinc-500 text-sm">
              Watch any stream free for the first 60 seconds before billing starts.
            </p>
          </div>
          
          <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <Coins className="w-6 h-6 text-amber-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pay Per Minute
            </h4>
            <p className="text-zinc-500 text-sm">
              After the free period, tokens are deducted per minute based on the creator's rate.
            </p>
          </div>
          
          <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
            <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4">
              <Radio className="w-6 h-6 text-indigo-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Earn as Creator
            </h4>
            <p className="text-zinc-500 text-sm">
              Set your own price and earn 60% of all viewer payments while streaming.
            </p>
          </div>
        </div>
      </main>
      
      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-800" data-testid="login-modal">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Demo Login
            </h3>
            <p className="text-zinc-500 text-sm mb-4">
              Select a user to login (demo mode)
            </p>
            
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white mb-4 focus:outline-none focus:border-indigo-500"
              data-testid="user-select"
            >
              <option value="">Select a user...</option>
              {users.map(u => (
                <option key={u.email} value={u.email}>
                  {u.first_name} {u.last_name} - {u.balance} tokens
                </option>
              ))}
            </select>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowLoginModal(false)}
                className="flex-1 px-4 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleLogin}
                className="flex-1 px-4 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all"
                data-testid="confirm-login-btn"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Stream Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-800" data-testid="create-stream-modal">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create Live Stream
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Stream Title</label>
                <input
                  type="text"
                  value={streamTitle}
                  onChange={(e) => setStreamTitle(e.target.value)}
                  placeholder="What are you streaming today?"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  data-testid="stream-title-input"
                />
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Tokens per Minute</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0.1"
                    max="1000"
                    step="0.1"
                    value={streamPrice}
                    onChange={(e) => setStreamPrice(e.target.value)}
                    className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    data-testid="stream-price-input"
                  />
                  <div className="px-4 py-3 bg-zinc-800 rounded-xl">
                    <Coins className="w-5 h-5 text-amber-400" />
                  </div>
                </div>
                <p className="text-zinc-500 text-xs mt-2">
                  Viewers get 60 seconds free, then pay this rate per minute
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateStream}
                disabled={creating}
                className="flex-1 px-4 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="start-stream-btn"
              >
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Radio className="w-4 h-4" />
                    Start Stream
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
